'use client';

import { Button, Chip } from '@heroui/react';
import { Quote } from '@/util/types/serverTypes';
import {
  FaHouseChimney,
  FaClock,
  FaDollarSign,
  FaPlus,
  FaCheck,
} from 'react-icons/fa6';
import { useTZDayjs } from '@/context/TimezoneContext';
import StandardModal from '@/components/ui/StandardModal';
import { useScheduler } from '../SchedulerContext';

type DraftSelectionModalProps = {
  drafts: Quote[];
  isOpen: boolean;
  onSelectDraft: (draft: Quote) => void;
  onStartNew: () => void;
  onClose: () => void;
  currentDraftId?: string | null;
};

export default function DraftSelectionModal({
  drafts,
  isOpen,
  onSelectDraft,
  onStartNew,
  onClose,
  currentDraftId,
}: DraftSelectionModalProps) {
  const tzDayjs = useTZDayjs();
  const { companyStyles, scheduleSettings } = useScheduler();

  // Draft totals are calculated before inspector assignment, so they must honor
  // the same pricing-visibility setting as every other scheduler surface.
  // `!== false` keeps pricing visible when settings are missing, matching SchedulerSummaryBar.
  const showPricing = scheduleSettings?.showPricingInOnlineScheduler !== false;

  const formatCurrency = (amount: number | undefined) => {
    if (!amount) return 'Not calculated';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return null;
    try {
      return tzDayjs(dateString).fromNow();
    } catch {
      return null;
    }
  };

  const getServiceInfo = (draft: Quote) => {
    // Try to get service info from charges
    if (
      draft.charges &&
      Array.isArray(draft.charges) &&
      draft.charges.length > 0
    ) {
      const charges = draft.charges as any[];
      const primaryCharge = charges.find((charge: any) => charge.isPrimary);
      const serviceName = primaryCharge
        ? typeof primaryCharge._serviceId === 'object'
          ? primaryCharge._serviceId.name
          : 'Service'
        : typeof charges[0]._serviceId === 'object'
          ? charges[0]._serviceId.name
          : 'Service';

      const addonCount = draft.charges.length - 1;

      return {
        serviceName,
        addonText:
          addonCount > 0
            ? `+${addonCount} addon${addonCount > 1 ? 's' : ''}`
            : null,
      };
    }

    // Try to parse from message field (draft services)
    if (draft.message) {
      try {
        const parsed = JSON.parse(draft.message);
        if (parsed.draftServices) {
          const addonCount = parsed.draftServices.selectedAddonIds?.length || 0;
          return {
            serviceName: 'Service selected',
            addonText:
              addonCount > 0
                ? `+${addonCount} addon${addonCount > 1 ? 's' : ''}`
                : null,
          };
        }
      } catch {
        // Ignore parse errors
      }
    }

    return null;
  };

  const getPropertyAddress = (draft: Quote) => {
    if (!draft.property) return null;

    const { street, city, state, zip } = draft.property;
    if (!street) return null;

    return `${street}${city ? `, ${city}` : ''}${state ? `, ${state}` : ''} ${
      zip || ''
    }`.trim();
  };

  const getAppointmentInfo = (draft: Quote) => {
    if (!draft.holdDatetime) return null;

    try {
      const date = new Date(draft.holdDatetime);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return null;
    }
  };

  return (
    <StandardModal
      show={isOpen}
      closeHandler={onClose}
      title='Welcome Back!'
      showButtons={false}
      size='3xl'
      scrollBehavior='inside'
    >
      <div className='mb-2 text-sm text-gray-600'>
        You have {drafts.length} draft quote{drafts.length !== 1 ? 's' : ''} in
        progress
      </div>

      {/* Start New Quote Button - Prominent at top */}
      <div className='mb-2'>
        <Button
          size='md'
          color='primary'
          startContent={<FaPlus />}
          onPress={onStartNew}
          className={`w-full font-bold uppercase shadow-lg ${
            companyStyles.secondaryButtonClassName || ''
          }`}
          style={{
            ...companyStyles.secondaryButtonStyles,
            // minHeight: '48px',
            // borderRadius: '12px',
          }}
        >
          Start A New Quote
        </Button>
      </div>

      <div className='my-4 flex items-center gap-4'>
        <div className='h-px flex-1 bg-gray-300' />
        <span className='text-xs uppercase font-light text-gray-500'>
          or resume an existing quote
        </span>
        <div className='h-px flex-1 bg-gray-300' />
      </div>

      {/* Draft List */}
      <div className='space-y-1.5 pb-4'>
        {drafts.map((draft) => {
          const serviceInfo = getServiceInfo(draft);
          const propertyAddress = getPropertyAddress(draft);
          const appointmentInfo = getAppointmentInfo(draft);
          const createdDate = formatDate(draft.createdAt);
          const isCurrentDraft = draft._id === currentDraftId;

          return (
            <div
              key={draft._id}
              className='rounded-md border bg-white p-2 transition-colors hover:border-gray-300'
            >
              <div className='flex flex-col gap-1.5'>
                {/* Current Draft Badge */}
                {isCurrentDraft && (
                  <div className='flex items-center gap-1'>
                    <Chip
                      size='sm'
                      color='primary'
                      variant='flat'
                      startContent={<FaCheck size={8} />}
                      className='text-[10px] h-5'
                    >
                      Currently Editing
                    </Chip>
                  </div>
                )}

                <div className='flex gap-1 flex-wrap justify-between'>
                  {/* Property Address */}
                  {propertyAddress ? (
                    <div className='flex items-start gap-1.5'>
                      <div className='shrink-0 text-gray-400 pt-0.5'>
                        <FaHouseChimney size={12} />
                      </div>
                      <div className='flex-1 min-w-0'>
                        <p className='text-[10px] font-medium text-gray-600 leading-tight'>
                          Property
                        </p>
                        <p className='text-xs font-semibold text-gray-900 truncate leading-tight'>
                          {propertyAddress}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className='flex items-start gap-1.5'>
                      <div className='shrink-0 text-gray-300 pt-0.5'>
                        <FaHouseChimney size={12} />
                      </div>
                      <div className='flex-1'>
                        <p className='text-[10px] italic text-gray-400 leading-tight'>
                          No property added yet
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Service Info */}
                  {serviceInfo && (
                    <div className='flex items-start gap-1.5'>
                      <div className='shrink-0 text-gray-400 pt-0.5'>
                        <FaCheck size={12} />
                      </div>
                      <div className='flex-1 min-w-0'>
                        <p className='text-[10px] font-medium text-gray-600 leading-tight'>
                          Services
                        </p>
                        <p className='text-xs text-gray-900 truncate leading-tight'>
                          {serviceInfo.serviceName}
                          {serviceInfo.addonText && (
                            <span className='ml-1 text-[10px] text-gray-500'>
                              {serviceInfo.addonText}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className='flex flex-wrap items-center gap-2 border-t pt-1.5 mt-0.5'>
                  {/* Total Price */}
                  {showPricing && draft.total !== undefined && (
                    <div className='flex items-center gap-1'>
                      <FaDollarSign className='text-gray-400' size={10} />
                      <span className='text-xs font-semibold text-gray-900'>
                        {formatCurrency(draft.total)}
                      </span>
                    </div>
                  )}

                  {/* Appointment Time */}
                  {appointmentInfo && (
                    <div className='flex items-center gap-1'>
                      <FaClock className='text-gray-400' size={10} />
                      <span className='text-xs text-gray-600'>
                        {appointmentInfo}
                      </span>
                    </div>
                  )}

                  {/* Created Date */}
                  {createdDate && (
                    <div className='ml-auto text-[10px] text-gray-500'>
                      Started {createdDate}
                    </div>
                  )}
                </div>

                {/* Resume Button */}
                <Button
                  variant='flat'
                  size='sm'
                  className={`w-full font-bold uppercase text-[10px] h-7 ${companyStyles.secondaryButtonClassName}`}
                  style={companyStyles.secondaryButtonStyles}
                  onPress={() => onSelectDraft(draft)}
                >
                  {isCurrentDraft
                    ? 'Continue Editing'
                    : 'Continue with this quote'}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </StandardModal>
  );
}
